import { useEffect, useRef, memo } from "react";

type ParticlesProps = {
  zIndex?: number;
  canvasClassName?: string;
  containerRef?: React.RefObject<HTMLElement | HTMLDivElement>;
  minLifetime?: number;
  maxLifetime?: number;
  particleCount?: number;
};

const Particles = ({
  zIndex = 1,
  canvasClassName,
  containerRef,
  minLifetime = 5,
  maxLifetime = 9,
  particleCount = 80,
}: ParticlesProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReducedMotion) {
      return;
    }

    const isMobile = window.innerWidth < 768;
    const actualParticleCount = isMobile
      ? Math.floor(particleCount * 0.4)
      : particleCount;

    const resizeCanvas = () => {
      if (containerRef?.current) {
        const rect = containerRef.current.getBoundingClientRect();
        canvas.width = rect.width;
        canvas.height = rect.height;
      } else {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      }
    };
    resizeCanvas();

    const particles: Array<{
      x: number;
      y: number;
      size: number;
      opacity: number;
      speed: number;
      maxOpacity: number;
      lifetime: number;
      age: number;
    }> = [];

    for (let i = 0; i < actualParticleCount; i++) {
      const maxOpacity = Math.random() * 0.4 + 0.3;
      const lifetime =
        minLifetime + Math.random() * (maxLifetime - minLifetime);
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 0.8 + 0.5,
        opacity: maxOpacity,
        maxOpacity: maxOpacity,
        speed: Math.random() * 0.4 + 0.2,
        lifetime: lifetime,
        age: Math.random() * lifetime * 0.5,
      });
    }

    let animationId: number;
    let running = true;
    let lastTime = 0;
    /* Only drawn while on screen. It used to run from page load until the
       page closed: the canvas behind the closing panel, three screens down,
       was drawing sixty times a second before anyone had scrolled to it, on
       the main thread the first render needs. */
    let visible = false;
    let resumed = true;
    const targetFPS = isMobile ? 30 : 60;
    const frameInterval = 1000 / targetFPS;

    const animate = (currentTime: number) => {
      if (!running || !visible) return;
      /* The first frame after coming back into view starts the clock again,
         so particles do not leap by however long it was off screen. */
      if (resumed) {
        resumed = false;
        lastTime = currentTime;
        animationId = requestAnimationFrame(animate);
        return;
      }

      const deltaTime = currentTime - lastTime;
      if (deltaTime < frameInterval) {
        animationId = requestAnimationFrame(animate);
        return;
      }
      lastTime = currentTime - (deltaTime % frameInterval);

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const frameMultiplier = deltaTime / 16.67;

      for (let i = 0; i < particles.length; i++) {
        const particle = particles[i];

        particle.y -= particle.speed * frameMultiplier;
        particle.age += 0.016 * frameMultiplier;

        const lifeProgress = particle.age / particle.lifetime;
        if (lifeProgress < 0.1) {
          particle.opacity = particle.maxOpacity * (lifeProgress / 0.1);
        } else if (lifeProgress > 0.7) {
          particle.opacity = particle.maxOpacity * ((1 - lifeProgress) / 0.3);
        } else {
          particle.opacity = particle.maxOpacity;
        }

        if (particle.age >= particle.lifetime) {
          particle.y = canvas.height + 10;
          particle.x = Math.random() * canvas.width;
          particle.age = 0;
          particle.lifetime =
            minLifetime + Math.random() * (maxLifetime - minLifetime);
        }

        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${particle.opacity})`;
        ctx.fill();
      }

      animationId = requestAnimationFrame(animate);
    };

    const io = new IntersectionObserver(([entry]) => {
      const was = visible;
      visible = entry.isIntersecting;
      if (visible && !was) {
        resumed = true;
        animationId = requestAnimationFrame(animate);
      } else if (!visible && was) {
        cancelAnimationFrame(animationId);
      }
    });
    io.observe(canvas);

    let resizeTimeout: number;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        resizeCanvas();

        for (const particle of particles) {
          if (particle.x > canvas.width)
            particle.x = Math.random() * canvas.width;
          if (particle.y > canvas.height)
            particle.y = Math.random() * canvas.height;
        }
      }, 100);
    };

    window.addEventListener("resize", handleResize, { passive: true });

    return () => {
      running = false;
      io.disconnect();
      cancelAnimationFrame(animationId);
      clearTimeout(resizeTimeout);
      window.removeEventListener("resize", handleResize);
    };
  }, [containerRef, minLifetime, maxLifetime, particleCount]);

  return (
    <canvas
      ref={canvasRef}
      className={canvasClassName || "particle-canvas"}
      aria-hidden="true"
      style={{
        position: containerRef ? "absolute" : "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex,
        pointerEvents: "none",
      }}
    />
  );
};

export default memo(Particles);
